import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Save, CheckCircle2, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { fmtUsd } from "@/lib/format";
import DriverCollectCardModal from "@/components/DriverCollectCardModal";

// v2.6.9 batch AA · skip these keys when iterating the breakdown for display
const EXCLUDED_BREAKDOWN_KEYS = new Set([
  "type", "note", "maintenance_fee", "tech_fee_per_passenger",
  "tech_fee_passengers", "sales_tax_pct", "cc_processing_fee",
  // v2.6.9 batch BG · suppress the % / label metadata for surcharges — the
  // $ amount is rendered as a normal line by the loop below.
  "late_night_surcharge_pct", "late_night_label",
  "holiday_surcharge_pct", "holiday_label",
]);

/**
 * FareSummary
 * Props:
 *   result · the quote object from backend (subtotal, breakdown, total, cc_surcharge)
 *   onSave · invoked with the optional `collected` card payload
 *   stripeAmountCents · explicit charge amount (overrides total*100)
 *   hideCollectCard · v2.6.9 batch BG · when true, hides the in-app card-capture
 *     button AND swaps in a "Process AFTER trip via Physical Square Reader"
 *     compliance banner. Used on all driver-side flows (Meter / City / TAXIquote)
 *     to enforce the hardware-only card policy.
 */
export default function FareSummary({ result, onSave, stripeAmountCents, hideCollectCard = false }) {
  const [collected, setCollected] = useState(null);
  const bd = result?.breakdown || {};
  // v2.6.9 batch AA · memoize the filter+map work · stable while bd is stable
  const breakdownRows = useMemo(() => {
    const passengers = bd.tech_fee_passengers || 1;
    return Object.entries(bd)
      .filter(([k, v]) => v !== undefined && v !== null && v !== "" && !EXCLUDED_BREAKDOWN_KEYS.has(k))
      .map(([k, v]) => {
        const label = k === "tech_fee"
          ? `Technology Fee (${passengers} pax)`
          : (k === "sales_tax" ? "County Tax"
              : (k === "late_night_surcharge" ? (bd.late_night_label || `Late Night Surcharge (${bd.late_night_surcharge_pct ?? 0}%)`)
                : (k === "holiday_surcharge" ? (bd.holiday_label || `Holiday Surcharge (${bd.holiday_surcharge_pct ?? 0}%)`)
                  : k.replace(/_/g, " "))));
        const isMoney = typeof v === "number" && (
          k.includes("$") || k.includes("fare") || k.includes("total") ||
          k.includes("fee") || k.includes("subtotal") || k.includes("tax") ||
          k.includes("surcharge")
        );
        const display = typeof v === "number" ? (isMoney ? `$${v.toFixed(2)}` : v) : String(v);
        return { key: k, label, display };
      });
  }, [bd]);

  if (!result) return null;
  const amountCents = stripeAmountCents || Math.round(result.total * 100);

  return (
    <Card className="bg-zinc-900 border-amber-400/40 p-6 fade-in" data-testid="fare-summary">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">Fare Total</div>
          <div className="meter-display text-5xl font-extrabold text-amber-400 mt-1" data-testid="fare-total">{fmtUsd(result.total)}</div>
        </div>
        <div className="checker-stripe h-10 w-10 rounded-md"/>
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 space-y-1.5 text-sm">
        {breakdownRows.map(({ key, label, display }) => (
          <div key={key} className="flex justify-between">
            <span className="text-zinc-400 capitalize">{label}</span>
            <span className="meter-display font-semibold">{display}</span>
          </div>
        ))}
        <div className="border-t border-zinc-800 pt-2 mt-2 flex justify-between">
          <span className="text-zinc-300 font-semibold">Subtotal</span>
          <span className="meter-display font-bold">{fmtUsd(result.subtotal)}</span>
        </div>
        {result.cc_surcharge > 0 && (
          <div className="flex justify-between">
            <span className="text-zinc-400">Credit Card Processing Fee</span>
            <span className="meter-display font-semibold">{fmtUsd(result.cc_surcharge)}</span>
          </div>
        )}
      </div>

      {collected && !hideCollectCard && (
        <div className="mt-3 p-2.5 rounded-md bg-green-500/10 border border-green-400/40 text-xs flex items-center gap-2" data-testid="collected-payment-banner">
          <CheckCircle2 className="h-4 w-4 text-green-400"/>
          <span className="text-green-300">Pre-charged · {collected.passenger?.first_name} {collected.passenger?.last_name} · {collected.brand} •••• {collected.last4}</span>
        </div>
      )}

      {/* v2.6.9 batch BG · Driver flows must use a physical Square reader for
          all card payments. In-app card capture is disabled here — the banner
          tells the driver to swipe AFTER the trip, then mark Card at closeout. */}
      {hideCollectCard && (
        <div className="mt-3 p-3 rounded-md bg-amber-400/10 border border-amber-400/50 flex items-start gap-2" data-testid="physical-reader-banner">
          <ShieldCheck className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5"/>
          <div className="text-xs text-amber-100 leading-relaxed">
            <div className="font-bold uppercase text-amber-300 tracking-wider mb-0.5 text-[11px]">Use Physical Square Reader</div>
            Card payments for driver-collected trips must be run through the physical Square hardware reader <span className="font-bold">AFTER the trip is completed</span>. The in-app cash-register at closeout records the result.
          </div>
        </div>
      )}

      <div className={`grid ${hideCollectCard ? "grid-cols-1" : "grid-cols-2"} gap-2 mt-4`}>
        <Button data-testid="save-trip-btn" onClick={() => onSave?.(collected)} className="h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold"><Save className="h-4 w-4 mr-2"/>Save Trip</Button>
        {!hideCollectCard && (
          <DriverCollectCardModal
            amountCents={amountCents}
            onComplete={(r) => setCollected(r)}
            triggerLabel={collected ? "Re-collect Card" : "Collect Card"}
          />
        )}
      </div>
    </Card>
  );
}
